import { Injectable } from '@angular/core';
import { EntityUpdateNotification } from '@core/models/entity-update-notification.model';
import { RealTimeUpdateEntityTypes } from '../../../modules/invoice-automation/constants/real-time-update-entity-types';
import { FirebaseService } from '@core/services/firebase/firebase.service';
import { NotificationService } from '@core/services/notification/notification.service';
import { Action, Selector, State, StateContext, StateToken, Store } from '@ngxs/store';
import { Timestamp } from 'firebase/firestore';
import { Subject, Subscription, catchError, of, takeUntil, tap } from 'rxjs';
import { EntityUpdateStateModel } from './entity-updates-state.model';
import {
  ClearEntityUpdates,
  HandleEntityUpdateError,
  ListenForAuditProgress,
  ListenForEntityUpdates,
  ListenForFileProcessingProgress,
  ListenForGenericEntityUpdates,
  ListenForKeyedEntityUpdates,
  KeyedEntityUpdatesReceived,
  ListenForScheduleUploadProgress,
  StopListeningForAuditProgress,
  StopListeningForAllKeyedEntityUpdates,
  StopListeningForEntityUpdates,
  StopListeningForFileProcessingProgress,
  StopListeningForKeyedEntityUpdates,
  ListenForOutboundReportProgress,
  StopListeningForOutboundReportProgress,
  StopListeningForScheduleUploadProgress,
  UpdateEntityUpdates,
} from './entity-updates.actions';

const defaults: EntityUpdateStateModel = {
  entityId: null,
  entityUpdates: [],
};

export const ENTITY_UPDATES_STATE_TOKEN = new StateToken<EntityUpdateStateModel>('entityUpdates');

@State<EntityUpdateStateModel>({
  name: ENTITY_UPDATES_STATE_TOKEN,
  defaults,
})
@Injectable()
export class EntityUpdatesState {
  private entityUpdatesUnSubscription = new Subject<void>();
  private activeSubscription: Subscription;

  // Specific subscriptions for audit and file processing
  private auditProgressSubscription: Subscription;
  private auditProgressUnsubscription: Subject<void>;
  private fileProcessingSubscription: Subscription;
  private fileProcessingUnsubscription: Subject<void>;
  private outboundReportSubscription: Subscription;
  private outboundReportUnsubscription: Subject<void>;
  private scheduleUploadSubscription: Subscription;
  private scheduleUploadUnsubscription: Subject<void>;
  /** One listener per `entityType:entityId`, independent of the single generic one above. */
  private readonly keyedSubscriptions = new Map<string, Subscription>();

  constructor(
    private store: Store,
    private readonly firebaseService: FirebaseService,
    private readonly notificationService: NotificationService
  ) {}

  @Selector()
  static getEntityUpdates(state: EntityUpdateStateModel) {
    return state.entityUpdates;
  }

  @Selector()
  static getYouAreHereUpdates(state: EntityUpdateStateModel) {
    return state.entityUpdates
      .filter(update => update.data['entityType'] === 'YouAreHere')
      .sort((a, b) => {
        return a.timestamp - b.timestamp;
      })[0];
  }

  @Selector()
  static getLatestEntityUpdates(state: EntityUpdateStateModel) {
    return state.entityUpdates.length > 0 ? state.entityUpdates[state.entityUpdates.length - 1] : null;
  }

  @Action(UpdateEntityUpdates)
  updateEntityUpdates(ctx: StateContext<EntityUpdateStateModel>, { entityUpdates }: UpdateEntityUpdates) {
    const existingEntityUpdatesSize = ctx.getState().entityUpdates.length;
    const newEntityUpdatesSize = entityUpdates.length;

    if (newEntityUpdatesSize >= 4) {
      ctx.patchState({ entityUpdates });
    } else {
      ctx.patchState({
        entityUpdates: [
          ...ctx.getState().entityUpdates.slice(newEntityUpdatesSize - existingEntityUpdatesSize),
          ...entityUpdates,
        ],
      });
    }
  }

  @Action(ClearEntityUpdates)
  clearEntityUpdates(ctx: StateContext<EntityUpdateStateModel>) {
    ctx.patchState(defaults);
  }

  @Action(ListenForEntityUpdates)
  listenForEntityUpdates(ctx: StateContext<EntityUpdateStateModel>, { entityId, orgId }: ListenForEntityUpdates) {
    const { entityId: currentEntityId } = ctx.getState();

    const {
      user: { orgId: currentOrgId },
    } = this.store.selectSnapshot(s => s.auth);

    // Clean up existing subscription if changing entity
    if (currentEntityId !== entityId) {
      this.cleanupExistingSubscription();
      ctx.patchState({ entityId });
    } else {
      return;
    }

    this.entityUpdatesUnSubscription = new Subject<void>();

    this.activeSubscription = this.firebaseService
      .getEntityUpdates(orgId ?? currentOrgId, entityId)
      .pipe(
        takeUntil(this.entityUpdatesUnSubscription),
        tap(updates => {
          const entityUpdates: EntityUpdateNotification[] = updates.map(update => {
            const data = update.payload.doc.data();
            const obj = {
              entityId: data['entityId'],
              data,
              entityType: data['entityType'],
              operation: data['operation'],
              orgId: data['orgId'],
              timestamp: (data['timestamp'] as Timestamp).toMillis(),
              error: data['error'],
              message: data['message'],
            };
            return obj;
          });
          if (entityUpdates.length > 0) {
            const updates = entityUpdates.filter(entityUpdate => !entityUpdate.error);
            const errors = entityUpdates.filter(entityUpdate => entityUpdate.error);
            this.store.dispatch(new UpdateEntityUpdates(updates));
            this.store.dispatch(new HandleEntityUpdateError(errors));
          }
        }),
        catchError(() => {
          return of(null);
        })
      )
      .subscribe();
  }

  @Action(ListenForGenericEntityUpdates)
  listenForGenericEntityUpdates(
    { patchState, dispatch, getState }: StateContext<EntityUpdateStateModel>,
    { entityType, entityId: newEntityId }: ListenForGenericEntityUpdates
  ) {
    const { entityId: currentEntityId } = getState();

    if (currentEntityId !== newEntityId || !this.activeSubscription || this.activeSubscription.closed) {
      this.cleanupExistingSubscription();
      patchState({ entityId: newEntityId });
    } else {
      return;
    }

    this.entityUpdatesUnSubscription = new Subject<void>();

    this.activeSubscription = this.firebaseService
      .getGenericEntityUpdates(entityType, newEntityId)
      .pipe(
        takeUntil(this.entityUpdatesUnSubscription),
        tap(updates => {
          const entityUpdates: EntityUpdateNotification[] = updates.map(update => {
            const data = update.payload.doc.data();
            const obj = {
              entityId: data['entityId'],
              data,
              entityType: data['entityType'],
              operation: data['operation'],
              timestamp: (data['timestamp'] as Timestamp).toMillis(),
              error: data['error'],
              message: data['message'],
            };
            return obj;
          });
          if (entityUpdates.length > 0) {
            dispatch(new UpdateEntityUpdates(entityUpdates));
          }
        }),
        catchError(() => {
          return of(null);
        })
      )
      .subscribe();
  }

  @Action(HandleEntityUpdateError)
  handleEntityUpdateError(ctx: StateContext<EntityUpdateStateModel>, { entityUpdateErrors }: HandleEntityUpdateError) {
    entityUpdateErrors.forEach(entityUpdate => {
      this.notificationService.showError(entityUpdate.message);
    });
  }

  @Action(ListenForAuditProgress)
  listenForAuditProgress({ dispatch }: StateContext<EntityUpdateStateModel>, { entityId }: ListenForAuditProgress) {
    if (this.auditProgressSubscription && !this.auditProgressSubscription.closed) {
      return;
    }

    this.cleanupAuditProgressSubscription();
    console.log('Listening for audit progress:', entityId);

    this.auditProgressUnsubscription = new Subject<void>();

    this.auditProgressSubscription = this.firebaseService
      .getGenericEntityUpdates('AUDIT_IN_PROGRESS', entityId)
      .pipe(
        takeUntil(this.auditProgressUnsubscription),
        tap(updates => {
          const entityUpdates: EntityUpdateNotification[] = updates.map(update => {
            const data = update.payload.doc.data();
            return {
              entityId: data['entityId'],
              data,
              entityType: data['entityType'],
              operation: data['operation'],
              timestamp: (data['timestamp'] as Timestamp).toMillis(),
              error: data['error'],
              message: data['message'],
            };
          });
          if (entityUpdates.length > 0) {
            const updates = entityUpdates.filter(entityUpdate => !entityUpdate.error);
            dispatch(new UpdateEntityUpdates(updates));
          }
        }),
        catchError(error => {
          console.error('Error in audit progress subscription:', error);
          return of(null);
        })
      )
      .subscribe();
  }

  @Action(ListenForOutboundReportProgress)
  listenForOutboundReportProgress(
    { dispatch }: StateContext<EntityUpdateStateModel>,
    { entityId }: ListenForOutboundReportProgress
  ) {
    if (this.outboundReportSubscription && !this.outboundReportSubscription.closed) {
      return;
    }

    this.cleanupOutboundReportSubscription();

    this.outboundReportUnsubscription = new Subject<void>();

    this.outboundReportSubscription = this.firebaseService
      .getGenericEntityUpdates(RealTimeUpdateEntityTypes.OUTBOUND_REPORT_IN_PROGRESS, entityId)
      .pipe(
        takeUntil(this.outboundReportUnsubscription),
        tap(updates => {
          const entityUpdates: EntityUpdateNotification[] = updates.map(update => {
            const data = update.payload.doc.data();
            return {
              entityId: data['entityId'],
              data,
              entityType: data['entityType'],
              operation: data['operation'],
              timestamp: (data['timestamp'] as Timestamp).toMillis(),
              error: data['error'],
              message: data['message'],
            };
          });
          const valid = entityUpdates.filter(entityUpdate => !entityUpdate.error);
          if (valid.length > 0) {
            dispatch(new UpdateEntityUpdates(valid));
          }
        }),
        catchError(error => {
          console.error('Error in outbound report progress subscription:', error);
          return of(null);
        })
      )
      .subscribe();
  }

  @Action(StopListeningForOutboundReportProgress)
  stopListeningForOutboundReportProgress() {
    this.cleanupOutboundReportSubscription();
  }

  private cleanupOutboundReportSubscription(): void {
    if (this.outboundReportUnsubscription) {
      this.outboundReportUnsubscription.next();
      this.outboundReportUnsubscription.complete();
      this.outboundReportUnsubscription = null;
    }

    if (this.outboundReportSubscription && !this.outboundReportSubscription.closed) {
      this.outboundReportSubscription.unsubscribe();
      this.outboundReportSubscription = null;
    }
  }

  @Action(ListenForFileProcessingProgress)
  listenForFileProcessingProgress(
    { dispatch }: StateContext<EntityUpdateStateModel>,
    { entityId }: ListenForFileProcessingProgress
  ) {
    if (this.fileProcessingSubscription && !this.fileProcessingSubscription.closed) {
      return;
    }

    this.cleanupFileProcessingSubscription();
    console.log('Listening for file processing progress:', entityId);

    this.fileProcessingUnsubscription = new Subject<void>();

    this.fileProcessingSubscription = this.firebaseService
      .getGenericEntityUpdates('FILE_PROCESSING_PROGRESS', entityId)
      .pipe(
        takeUntil(this.fileProcessingUnsubscription),
        tap(updates => {
          const entityUpdates: EntityUpdateNotification[] = updates.map(update => {
            const data = update.payload.doc.data();
            return {
              entityId: data['entityId'],
              data,
              entityType: data['entityType'],
              operation: data['operation'],
              timestamp: (data['timestamp'] as Timestamp).toMillis(),
              error: data['error'],
              message: data['message'],
            };
          });
          if (entityUpdates.length > 0) {
            const updates = entityUpdates.filter(entityUpdate => !entityUpdate.error);
            dispatch(new UpdateEntityUpdates(updates));
          }
        }),
        catchError(error => {
          console.error('Error in file processing subscription:', error);
          return of(null);
        })
      )
      .subscribe();
  }

  @Action(StopListeningForEntityUpdates)
  stopListeningForEntityUpdates(ctx: StateContext<EntityUpdateStateModel>) {
    this.cleanupExistingSubscription();
  }

  @Action(ListenForKeyedEntityUpdates)
  listenForKeyedEntityUpdates(
    ctx: StateContext<EntityUpdateStateModel>,
    { entityType, entityId }: ListenForKeyedEntityUpdates
  ) {
    const key = keyedSubscriptionKey(entityType, entityId);
    if (this.keyedSubscriptions.get(key)?.closed === false) {
      return;
    }
    const subscription = this.firebaseService
      .getGenericEntityUpdates(entityType, entityId)
      .pipe(
        tap(updates => {
          const entityUpdates: EntityUpdateNotification[] = updates.map(update => {
            const data = update.payload.doc.data();
            return {
              entityId: data['entityId'],
              data,
              entityType: data['entityType'],
              operation: data['operation'],
              timestamp: (data['timestamp'] as Timestamp).toMillis(),
              error: data['error'],
              message: data['message'],
            };
          });
          if (entityUpdates.length > 0) {
            ctx.dispatch(new KeyedEntityUpdatesReceived(entityType, entityId, entityUpdates));
          }
        }),
        catchError(() => of(null))
      )
      .subscribe();
    this.keyedSubscriptions.set(key, subscription);
  }

  @Action(StopListeningForKeyedEntityUpdates)
  stopListeningForKeyedEntityUpdates(
    ctx: StateContext<EntityUpdateStateModel>,
    { entityType, entityId }: StopListeningForKeyedEntityUpdates
  ) {
    const key = keyedSubscriptionKey(entityType, entityId);
    this.keyedSubscriptions.get(key)?.unsubscribe();
    this.keyedSubscriptions.delete(key);
  }

  @Action(StopListeningForAllKeyedEntityUpdates)
  stopListeningForAllKeyedEntityUpdates() {
    this.keyedSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.keyedSubscriptions.clear();
  }

  @Action(StopListeningForAuditProgress)
  stopListeningForAuditProgress(ctx: StateContext<EntityUpdateStateModel>) {
    this.cleanupAuditProgressSubscription();
  }

  @Action(StopListeningForFileProcessingProgress)
  stopListeningForFileProcessingProgress(ctx: StateContext<EntityUpdateStateModel>) {
    this.cleanupFileProcessingSubscription();
  }

  @Action(ListenForScheduleUploadProgress)
  listenForScheduleUploadProgress(
    { dispatch }: StateContext<EntityUpdateStateModel>,
    { entityId }: ListenForScheduleUploadProgress
  ) {
    if (this.scheduleUploadSubscription && !this.scheduleUploadSubscription.closed) {
      return;
    }

    this.cleanupScheduleUploadSubscription();
    console.log('Listening for schedule upload progress:', entityId);

    this.scheduleUploadUnsubscription = new Subject<void>();

    this.scheduleUploadSubscription = this.firebaseService
      .getGenericEntityUpdates('SCHEDULE_UPLOAD_IN_PROGRESS', entityId)
      .pipe(
        takeUntil(this.scheduleUploadUnsubscription),
        tap(updates => {
          const entityUpdates: EntityUpdateNotification[] = updates.map(update => {
            const data = update.payload.doc.data();
            return {
              entityId: data['entityId'],
              data,
              entityType: data['entityType'],
              operation: data['operation'],
              timestamp: (data['timestamp'] as Timestamp).toMillis(),
              error: data['error'],
              message: data['message'],
            };
          });
          if (entityUpdates.length > 0) {
            const updates = entityUpdates.filter(entityUpdate => !entityUpdate.error);
            dispatch(new UpdateEntityUpdates(updates));
          }
        }),
        catchError(error => {
          console.error('Error in schedule upload subscription:', error);
          return of(null);
        })
      )
      .subscribe();
  }

  @Action(StopListeningForScheduleUploadProgress)
  stopListeningForScheduleUploadProgress(ctx: StateContext<EntityUpdateStateModel>) {
    this.cleanupScheduleUploadSubscription();
  }

  private cleanupExistingSubscription(): void {
    if (this.entityUpdatesUnSubscription) {
      this.entityUpdatesUnSubscription.next();
      this.entityUpdatesUnSubscription.complete();
      this.entityUpdatesUnSubscription = null;
    }

    if (this.activeSubscription && !this.activeSubscription.closed) {
      this.activeSubscription.unsubscribe();
      this.activeSubscription = null;
    }
  }

  private cleanupAuditProgressSubscription(): void {
    console.log('Cleaning up audit progress subscription');
    if (this.auditProgressUnsubscription) {
      this.auditProgressUnsubscription.next();
      this.auditProgressUnsubscription.complete();
      this.auditProgressUnsubscription = null;
    }

    if (this.auditProgressSubscription && !this.auditProgressSubscription.closed) {
      this.auditProgressSubscription.unsubscribe();
      this.auditProgressSubscription = null;
    }
  }

  private cleanupFileProcessingSubscription(): void {
    console.log('Cleaning up file processing subscription');
    if (this.fileProcessingUnsubscription) {
      this.fileProcessingUnsubscription.next();
      this.fileProcessingUnsubscription.complete();
      this.fileProcessingUnsubscription = null;
    }

    if (this.fileProcessingSubscription && !this.fileProcessingSubscription.closed) {
      this.fileProcessingSubscription.unsubscribe();
      this.fileProcessingSubscription = null;
    }
  }

  private cleanupScheduleUploadSubscription(): void {
    console.log('Cleaning up schedule upload subscription');
    if (this.scheduleUploadUnsubscription) {
      this.scheduleUploadUnsubscription.next();
      this.scheduleUploadUnsubscription.complete();
      this.scheduleUploadUnsubscription = null;
    }

    if (this.scheduleUploadSubscription && !this.scheduleUploadSubscription.closed) {
      this.scheduleUploadSubscription.unsubscribe();
      this.scheduleUploadSubscription = null;
    }
  }
}

function keyedSubscriptionKey(entityType: string, entityId: string): string {
  return `${entityType}:${entityId}`;
}
